/* eslint no-underscore-dangle: 0 */
import React, { useMemo } from 'react';

// material-ui
import { Autocomplete, Chip, TextField } from '@mui/material';

// project imports
import useGQL from 'hooks/crm/useGQL';
import { CrmTag } from 'types/crm';

type TagSelectProps = {
    /** Selected tag keys. */
    value: string[];
    onChange: (keys: string[]) => void;
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    disabled?: boolean;
    fullWidth?: boolean;
};

/**
 * Reusable multi-select over the managed CRM tag list (POGO-12603 Phase 1).
 * Stores tag *keys* (the stable identity used across accounts, contacts,
 * automations and audiences) while displaying the human labels. Tags are
 * managed in CRM Settings — this picker never creates free-text tags.
 *
 * A value that isn't in the managed list (e.g. a legacy or imported tag) is
 * still shown, using the raw key as its label, so nothing is silently dropped.
 */
const TagSelect = ({
    value,
    onChange,
    label = 'Tags',
    placeholder,
    size = 'small',
    disabled = false,
    fullWidth = true
}: TagSelectProps) => {
    const { GET_TAGS } = useGQL();
    const { data, loading } = GET_TAGS();

    const managedTags: CrmTag[] = useMemo(() => data?.getCrmTags?.tags || [], [data]);

    const labelByKey = useMemo(() => {
        const map = new Map<string, string>();
        managedTags.forEach((tag) => map.set(tag.key, tag.label));
        return map;
    }, [managedTags]);

    // Union of managed keys + any already-selected keys not in the list, so a
    // legacy/imported value stays visible and selectable.
    const options = useMemo(() => {
        const keys = managedTags.map((tag) => tag.key);
        value.forEach((key) => {
            if (!keys.includes(key)) {
                keys.push(key);
            }
        });
        return keys;
    }, [managedTags, value]);

    return (
        <Autocomplete
            multiple
            size={size}
            fullWidth={fullWidth}
            disabled={disabled}
            options={options}
            value={value}
            loading={loading}
            onChange={(_, next) => onChange(next as string[])}
            getOptionLabel={(key) => labelByKey.get(key) || key}
            isOptionEqualToValue={(option, selected) => option === selected}
            renderTags={(selected, getTagProps) =>
                selected.map((key, index) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <Chip {...getTagProps({ index })} key={key} size="small" label={labelByKey.get(key) || key} variant="outlined" />
                ))
            }
            renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TextField {...params} label={label} placeholder={placeholder || 'Select tags'} />
            )}
        />
    );
};

export default TagSelect;
